import { SortDir } from '@shared/constants';
import dayjs from 'dayjs';
import orderBy from 'lodash/orderBy';
import sortedUniq from 'lodash/sortedUniq';
import { useCallback, useMemo } from 'react';

import { useTenantQueryActions } from '@/api/hooks/tenantQueryActions';
import { useUserQueryActions } from '@/api/hooks/userQueryActions';
import type { UserResponse } from '@/api/schema/data-contracts';
import { ApiEstimationItemPurpose, ApiEstimationItemStatus } from '@/api/schema/data-contracts';
import type { ExportProps, ExportTable } from '@/components/organism/DataTable/hooks/exports/model';
import { useExport } from '@/components/organism/DataTable/hooks/exports/useExport';
import type { eExportFormat } from '@/components/organism/DataTable/model';
import { ProjectInfoMessages } from '@/components/organism/NewEstimation/ProjectBasics/ProjectInfo/messages';
import { useMessages } from '@/hooks/messages';
import type { EstimationWithDependencies } from '@/hooks/useGetEstimationWithDependencies';
import { useGetEstimationWithDependencies } from '@/hooks/useGetEstimationWithDependencies';
import { htmlToPlainText } from '@/utils/html';
import { getPhaseAndDescendantIds } from '@/utils/phasesFiltering';
import { sortCostTypes, sortResourceTypes } from '@/utils/sorting';

import { usePdfHeaderFooterDataGetter } from '../usePdfHeaderFooterDataGetter';
import { prepareExportDataWithTotals } from './exportDataBuilder';
import {
  getDescendants,
  getDistinctParameterNamesSorted,
  reorderChildrenAfterItsParentRecursively,
} from './helperFunctions';
import { estimationExportMessageDefinitions } from './messages';
import type {
  EstimationExporter,
  EstimationExportOutputOptions,
  UseEstimationExportProps,
  UseEstimationExportReturn,
} from './model';

export const useEstimationExport = ({
  estimationId,
  title,
  costModel,
  outputOptionsArray,
  hiddenCostTypeColumns = [],
  hiddenDirectCostSummaryColumns = [],
  hiddenFields = [],
  hiddenPhases = [],
}: UseEstimationExportProps): UseEstimationExportReturn => {
  const messages = useMessages(estimationExportMessageDefinitions);
  const projectInfoMessages = useMessages(ProjectInfoMessages);

  const { data } = useGetEstimationWithDependencies(estimationId, {
    shouldFetchEstimationItemCosts:
      outputOptionsArray.some((outputOptions) => outputOptions.showCostPurposeEstimationItems) &&
      outputOptionsArray.some((outputOptions) => outputOptions.showDirectCostColumns),
    shouldFetchResources:
      outputOptionsArray.some(
        (outputOptions) => outputOptions.showResourcePurposeEstimationItems,
      ) &&
      (outputOptionsArray.some((outputOptions) => outputOptions.showResourceColumns) ||
        outputOptionsArray.some((outputOptions) => outputOptions.showResourceCostColumns) ||
        outputOptionsArray.some((outputOptions) => outputOptions.showCostSumColumn)),
  });

  const { useGetTenant } = useTenantQueryActions();
  const { data: tenantData } = useGetTenant();

  const { getPdfHeaderFooterForEstimation } = usePdfHeaderFooterDataGetter(title);

  const { getUser } = useUserQueryActions();

  const exportProps = useMemo<ExportProps>(
    () => ({
      orientation: 'landscape',
      fileName: [tenantData?.name, title, dayjs().format('YYYYMMDDhhmm')]
        .filter((x) => !!x?.trim())
        .join('_'),
    }),
    [title, tenantData],
  );

  const exporter = useExport(exportProps);

  const exportEstimation = async (
    format: eExportFormat,
    parentEstimationItemId: string | undefined,
  ): Promise<undefined> => {
    // Closes over the hook-scope values (outputOptionsArray, data, costModel,
    // hidden*). The call site always passed those same values, so the former
    // parameters were redundant and shadowed the outer scope (no-shadow).
    if (!data) {
      return undefined;
    }
    const allTables: ExportTable[] = [];

    const shouldShowMasterdata = outputOptionsArray.some(
      (outputOptions) => outputOptions.showMasterdataColumn,
    );
    if (shouldShowMasterdata) {
      const masterdataFields: Array<{ field: string; value: string }> = [];

      // Add Project Description (mandatory master data) if available and not hidden
      const PROJECT_DESCRIPTION_FIELD_ID = 'projectDescription';
      if (
        data.estimation.projectDescription &&
        !hiddenFields.includes(PROJECT_DESCRIPTION_FIELD_ID)
      ) {
        masterdataFields.push({
          field: projectInfoMessages.projectDescriptionLabel(),
          value: htmlToPlainText(data.estimation.projectDescription),
        });
      }

      // Add additional fields (custom metadata) if available
      if (data.additionalFields && data.additionalFields.length > 0) {
        const additionalFields = data.additionalFields
          .filter((field) => field.value && field.value !== '')
          .map((field) => {
            const fieldDefinition = data.estimationModel.additionalFields?.find(
              (def) => def.id === field.estimationModelAdditionalFieldDefinitionId,
            );
            return fieldDefinition
              ? {
                  field: fieldDefinition.name,
                  value:
                    typeof field.value === 'string' && /<[^>]+>/.test(field.value)
                      ? htmlToPlainText(field.value)
                      : (field.value ?? ''),
                  fieldId: fieldDefinition.id,
                }
              : null;
          })
          .filter((item) => item !== null)
          .filter((item) => !hiddenFields.includes(item.fieldId));

        masterdataFields.push(
          ...additionalFields.map((item) => ({
            field: item.field,
            value: item.value,
          })),
        );
      }

      if (masterdataFields.length > 0) {
        const masterdataTable: ExportTable = {
          title: 'Masterdata',
          columns: [
            { accessor: 'field', headerLabel: 'Field' },
            { accessor: 'value', headerLabel: 'Value' },
          ],
          rows: masterdataFields,
        };
        allTables.push(masterdataTable);
      }
    }

    const tablePromises = outputOptionsArray.map(async (outputOptions) => {
      if (
        outputOptions.showCostSumColumn &&
        outputOptions.showResourcePurposeEstimationItems &&
        !costModel
      ) {
        throw new Error('Cost model must be set in order to calculate resource costs.');
      }

      const relevantItemsSorted = ((estimationWithDependencies: EstimationWithDependencies) => {
        // Filter out hidden phases and their descendants
        let filteredItems = estimationWithDependencies.estimationItems;
        if (hiddenPhases && hiddenPhases.length > 0 && !outputOptions.showOnlyPhases) {
          const hiddenPhaseAndDescendantIds = new Set<string>();
          hiddenPhases.forEach((phaseId) => {
            const ids = getPhaseAndDescendantIds(
              phaseId,
              estimationWithDependencies.estimationItems,
            );
            ids.forEach((id) => hiddenPhaseAndDescendantIds.add(id));
          });
          filteredItems = filteredItems.filter((item) => !hiddenPhaseAndDescendantIds.has(item.id));
        }

        const filteredByPurposeAndStatus = filteredItems
          .filter((x) => x.status !== ApiEstimationItemStatus.OMITTED)
          .filter(
            (x) =>
              (outputOptions.showResourcePurposeEstimationItems &&
                x.purpose === ApiEstimationItemPurpose.RESOURCE) ||
              (outputOptions.showCostPurposeEstimationItems &&
                x.purpose === ApiEstimationItemPurpose.COST),
          );

        const relevantItems = outputOptions.showOnlyPhases
          ? filteredByPurposeAndStatus.filter((item) => !item.parentId)
          : getDescendants(filteredByPurposeAndStatus, parentEstimationItemId);

        const relevantItemsSortedByPurposeThenGroupOrderAndThenOrder = orderBy(
          relevantItems,
          [
            (item) => item.purpose,
            (item) =>
              estimationWithDependencies.estimationModel.itemGroups.find(
                (group) => group.id === item.estimationModelGroupId,
              )?.order,
            (item) => item.order,
          ],
          [SortDir.DESC, SortDir.ASC, SortDir.ASC],
        );

        return reorderChildrenAfterItsParentRecursively(
          relevantItemsSortedByPurposeThenGroupOrderAndThenOrder,
          parentEstimationItemId,
        );
      })(data);

      const relevantParameterNames = outputOptions.showParameterColumns
        ? getDistinctParameterNamesSorted(
            relevantItemsSorted.filter((x) => x.purpose === ApiEstimationItemPurpose.RESOURCE),
            data.methodologies,
            data.estimationModel,
          )
        : [];

      // Select appropriate hidden columns array based on table type
      // For resource cost table: use hiddenCostTypeColumns
      // For direct cost table: use hiddenDirectCostSummaryColumns
      const hiddenColumnsForThisTable = outputOptions.showResourceCostColumns
        ? hiddenCostTypeColumns
        : outputOptions.showDirectCostColumns
          ? hiddenDirectCostSummaryColumns
          : [];

      const costTypes = sortCostTypes(
        data.estimationModel.costTypes,
        data.estimationModel.costTypeGroups,
      ).filter((costType) => !hiddenColumnsForThisTable.includes(costType.id));

      const resourceTypes = sortResourceTypes(
        data.estimationModel.resourceTypes,
        data.estimationModel.resourceTypeGroups,
      );

      const columns = [
        'reference',
        'group',
        'name',
        ...(outputOptions.showPhaseLeadColumn ? ['phaseLead'] : []),
        ...(outputOptions.showMethodologyColumn ? ['methodology'] : []),
        ...relevantParameterNames.map((paramName) => `parameter ${paramName}`),
        ...(outputOptions.showEstimateColumn ? ['estimation'] : []),
        ...(outputOptions.showResourceColumns
          ? [
              'resourcingCheck',
              ...resourceTypes.map((resourceType) => `resource ${resourceType.name}`),
            ]
          : []),
        ...((outputOptions.showResourceCostColumns && costModel) ||
        outputOptions.showDirectCostColumns
          ? [...costTypes.map((costType) => `cost ${costType.name}`)]
          : []),
        ...(outputOptions.showCostSumColumn &&
        ((costModel && outputOptions.showResourcePurposeEstimationItems) ||
          outputOptions.showCostPurposeEstimationItems)
          ? ['costSum']
          : []),
        ...(outputOptions.showAssumptionsColumn ? ['assumptions'] : []),
      ];

      const titles = [
        messages.reference(),
        messages.group(),
        messages.phase(),
        ...(outputOptions.showPhaseLeadColumn ? [messages.phaseLead()] : []),
        ...(outputOptions.showMethodologyColumn ? [messages.methodology()] : []),
        ...relevantParameterNames,
        ...(outputOptions.showEstimateColumn ? [messages.estimate()] : []),
        ...(outputOptions.showResourceColumns
          ? [
              messages.estimateCheck(),
              ...resourceTypes.map(
                (resourceType) =>
                  data.estimation.resourceDisplayNames?.[resourceType.id] ?? resourceType.name,
              ),
            ]
          : []),
        ...((outputOptions.showResourceCostColumns && costModel) ||
        outputOptions.showDirectCostColumns
          ? [...costTypes.map((costType) => `${costType.name}`)]
          : []),
        ...(outputOptions.showCostSumColumn &&
        ((costModel && outputOptions.showResourcePurposeEstimationItems) ||
          outputOptions.showCostPurposeEstimationItems)
          ? [messages.costsSum()]
          : []),
        ...(outputOptions.showAssumptionsColumn ? [messages.assumptions()] : []),
      ];

      const userIds = sortedUniq(
        outputOptions.showPhaseLeadColumn
          ? relevantItemsSorted.filter((x) => !!x.phaseLeadId).map((x) => x.phaseLeadId!)
          : [],
      );

      const userResponses: UserResponse[] = userIds.length
        ? await Promise.all(userIds.map((userId) => getUser(userId)))
        : [];

      const exportDataWithTotals = prepareExportDataWithTotals(
        relevantItemsSorted,
        data,
        costModel,
        outputOptions,
        userResponses,
        relevantParameterNames,
        messages.total(),
        hiddenColumnsForThisTable,
      );

      const table: ExportTable = {
        title: outputOptions.tableTitle,
        columns: columns.map((x, index) => ({
          accessor: x,
          headerLabel: titles[index],
        })),
        rows: exportDataWithTotals,
      };

      return table;
    });

    const dataTables = await Promise.all(tablePromises);
    allTables.push(...dataTables);
    const headerFooterDataPromise = getPdfHeaderFooterForEstimation(estimationId);

    exporter(format, allTables, headerFooterDataPromise);
  };

  const estimationExporter = useCallback<EstimationExporter>(
    (format, parentEstimationItemId) => {
      if (!data) {
        return Promise.resolve(undefined); // TODO: do we want to wait instead?
      }
      return exportEstimation(format, parentEstimationItemId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- exportEstimation is a complex local function; its deps are already tracked via the other deps here
    [
      data,
      outputOptionsArray,
      costModel,
      messages,
      hiddenCostTypeColumns,
      hiddenDirectCostSummaryColumns,
      hiddenFields,
      hiddenPhases,
    ],
  );

  // props checks
  if (
    outputOptionsArray.some((outputOptions) => outputOptions.showEstimateColumn) &&
    !outputOptionsArray.some((outputOptions) => outputOptions.showResourcePurposeEstimationItems)
  ) {
    throw new Error('Non-direct cost estimation items must be loaded in order to show Estimate');
  }
  if (
    outputOptionsArray.some((outputOptions) => outputOptions.showMethodologyColumn) &&
    !outputOptionsArray.some((outputOptions) => outputOptions.showResourcePurposeEstimationItems)
  ) {
    throw new Error('Non-direct cost estimation items must be loaded in order to show Methodology');
  }
  if (
    outputOptionsArray.some((outputOptions) => outputOptions.showParameterColumns) &&
    !outputOptionsArray.some((outputOptions) => outputOptions.showResourcePurposeEstimationItems)
  ) {
    throw new Error(
      'Non-direct cost estimation items must be loaded in order to show parameter values',
    );
  }
  if (
    !outputOptionsArray.some((outputOptions) => outputOptions.showResourcePurposeEstimationItems) &&
    !outputOptionsArray.some((outputOptions) => outputOptions.showCostPurposeEstimationItems)
  ) {
    throw new Error('Some estimation items must be shown');
  }
  if (
    outputOptionsArray.some((outputOptions) => outputOptions.showResourceColumns) &&
    !outputOptionsArray.some((outputOptions) => outputOptions.showResourcePurposeEstimationItems)
  ) {
    throw new Error('Non-direct cost estimation items must be loaded in order to show Methodology');
  }
  if (
    outputOptionsArray.some((outputOptions) => outputOptions.showResourceCostColumns) &&
    !outputOptionsArray.some((outputOptions) => outputOptions.showResourcePurposeEstimationItems)
  ) {
    throw new Error('Non-direct cost estimation items must be loaded in order to show resources');
  }
  if (
    outputOptionsArray.some((outputOptions) => outputOptions.showDirectCostColumns) &&
    !outputOptionsArray.some((outputOptions) => outputOptions.showCostPurposeEstimationItems)
  ) {
    throw new Error(
      'Direct cost estimation items must be loaded in order to show direct cost columns',
    );
  }

  return estimationExporter;
};

export type { EstimationExportOutputOptions };
